import { APIEntityType, APITagAttributes, ArdoqId } from '@ardoq/api-types';
import { createStubImplementation } from '@ardoq/stub-implementation';
import { returnZero } from '@ardoq/common-helpers';

// an entity like reference or component etc.
export type GetTagsForModel = (modelId: ArdoqId) => {
  id: ArdoqId;
  name: string;
}[];

export type Get = (tagId: ArdoqId) => APITagAttributes | null;

export type GetTagCount = () => number;

type GetWorkspaceTags = (id: ArdoqId) => APITagAttributes[];

type GetAllTags = () => APITagAttributes[];

type EntityHasTag = (
  tagName: string,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityID: ArdoqId
) => boolean;

export type TagInterfaceImplementation = {
  getTagsForModel: GetTagsForModel;
  get: Get;
  getTagCount: GetTagCount;
  getWorkspaceTags: GetWorkspaceTags;
  getAllTags: GetAllTags;
  entityHasTag: EntityHasTag;
};

const defaultImplementation: TagInterfaceImplementation = {
  getTagsForModel: () => [],
  get: () => null,
  getTagCount: returnZero,
  getWorkspaceTags: () => [],
  getAllTags: () => [],
  entityHasTag: () => false,
};

const {
  getTagsForModel,
  get,
  registerImplementation,
  getTagCount,
  getWorkspaceTags,
  getAllTags,
  entityHasTag,
} = createStubImplementation('@ardoq/tag-interface', defaultImplementation);

/**
 * `tagInterface` is a Backbone-era singleton: every member snapshots
 * `tags$.state` at call time.
 *
 * The app is stream-based now. Join `tags$` (`streams/tags/tags$.ts`) at a
 * permitted boundary -- a `*$.ts` file, a file inside a `*$/` folder, or a
 * `*routines.ts` -- and thread the resolved tags downstream as plain parameters.
 * The pure reads live in `tagUtils` (`@ardoq/scope-data`); `tagOperations`
 * (`@ardoq/core`) covers the tag *edit* side (`parseTagInput`, `diffTagNames`,
 * `buildTagIntents`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const tagInterface = {
  registerImplementation,
  defaultImplementation,
  /**
   * @deprecated Read `tags$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getWorkspaceTags,
  /**
   * @deprecated Read `tags$.models`.
   */
  getAllTags,
  /**
   * @deprecated Call `tagUtils.getIdsOfComponentsWithTag` / `tagUtils.getIdsOfReferencesWithTag` (`@ardoq/scope-data`) with tags from `tags$`.
   */
  entityHasTag,
  /**
   * @deprecated Read `tags$.models` and select with `tagUtils.getAttributes` (`@ardoq/scope-data`).
   */
  getTagsForModel,
  /**
   * @deprecated Read `tags$.byId[id]`, or call `tagUtils.getTag` (`@ardoq/scope-data`).
   */
  get,
  /**
   * @deprecated Read `tags$.models.length`.
   */
  getTagCount,
};
